import {
  action,
  observable,
  makeObservable,
  makeAutoObservable,
  runInAction,
  autorun,
  toJS,
} from 'mobx'

import Papa from 'papaparse'
import { saveAs } from 'file-saver'
import { EditOutlined, FileOutlined } from '@ant-design/icons'
import {
  collectFormAnswersData,
  getUserTableState,
  updateUserTableState,
  downloadFile,
} from '../../../../services/api'

import { message, Tooltip } from 'antd'
import { produce } from 'immer'
import { observer } from 'mobx-react-lite'
import { getUser, getUsernameUser } from '../../../../utils/sessionManagement'
import { useParams } from 'react-router-dom'
import { nanoid } from 'nanoid'
import { useMemo, useRef, useEffect } from 'react'
import React from 'react'

import { createMRTColumnHelper } from 'material-react-table'

class AnswersStore {
  data = []
  version = nanoid()
  pageCount = 0
  pageSize = 20
  pageIndex = 0
  pagination = { pageIndex: this.pageIndex, pageSize: this.pageSize }
  totalRows = 0
  loading = false
  headers = []
  headerstest = []
  hiddenColumns = []
  columnOrderDefault = ['mrt-row-select', 'mrt-row-actions']
  mySortBy = ''

  // State Material React Table
  columnFilters = []
  columnVisibility = {}
  columnSizing = {}
  density = 'comfortable'
  globalFilter = undefined
  showGlobalFilter = false
  columnPinning = { left: ['mrt-row-select'], right: ['mrt-row-actions'] }
  showColumnFilters = false
  isFullScreen = false
  includeLabels = false
  sorting = []
  columnOrder = []

  rowSelection = {}

  IdentifiantForIDDjango = 'header_b0755b90-cc68-42ac-92c6-d165966e1cc9'
  IdentifiantForCanEditDjango = 'canEdit'
  selectedRowIds = []

  showColumnFilters = false
  showToolHidenColumns = false

  constructor(url_code) {
    makeAutoObservable(this)
    this.url_code = url_code
  }

  handleDownload = action((path) => {
    downloadFile(path).catch((error) => {
      console.error('Erreur lors du téléchargement:', error)
    })
  })

  transformHeadersToColumns(headers) {
    return headers.map((header) => {
      let column = {
        accessorKey: header.idCanvas,
        id: header.idCanvas,
        key: header.idCanvas,
        header: header.label,
        userId: header.idCanvas,
        type: header.type || '',
        enableHiding: true,
        enableClickToCopy: true,
      }

      if (header.type === 'Upload') {
        column.Cell = ({ row }) => {
          const value = row._valuesCache[column.accessorKey]
          const splitName = value.split('/')

          if (value && value !== '' && value !== 'Null' && value !== 'null') {
            return (
              <Tooltip title={`Télécharger le fichier ${splitName[3]}`}>
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    listStyle: 'none',
                    flexDirection: 'row',
                    gap: '5px',
                    textAlign: 'center',
                    alignItems: 'center',
                    height: '36px',
                    padding: '0 16px',
                    borderRadius: '9999999px',
                    lineHeight: '20px',
                    border: '1px solid #e9ecef',
                  }}
                  onClick={() => this.handleDownload(value)}
                >
                  <FileOutlined />
                  Fichier
                </div>
              </Tooltip>
            )
          }
          return null
        }
      }

      return column
    })
  }

  // Méthode pour initialiser l'état de la table
  initializeState = async () => {
    try {
      const response = await getUserTableState(this.url_code)
      const initialState = response.state || {}

      runInAction(() => {
        this.columnFilters = initialState.columnFilters || this.columnFilters
        this.columnVisibility =
          initialState.columnVisibility || this.columnVisibility
        this.columnSizing = initialState.columnSizing || this.columnSizing
        this.density = initialState.density || this.density
        this.globalFilter = initialState.globalFilter || this.globalFilter
        this.showGlobalFilter =
          initialState.showGlobalFilter || this.showGlobalFilter
        this.columnPinning = initialState.columnPinning || this.columnPinning
        this.showColumnFilters =
          initialState.showColumnFilters || this.showColumnFilters
        this.isFullScreen = initialState.isFullScreen || this.isFullScreen
        this.sorting = initialState.sorting || this.sorting
        this.includeLabels = initialState.includeLabels || this.includeLabels
        // ... initialiser les autres propriétés d'état
      })
    } catch (error) {
      console.error("Erreur lors de la récupération de l'état initial :", error)
    }
  }

  updateTableState = async () => {
    const stateSnapshot = {
      columnFilters: this.columnFilters,
      columnVisibility: this.columnVisibility,
      columnSizing: this.columnSizing,
      density: this.density,
      globalFilter: this.globalFilter,
      showGlobalFilter: this.showGlobalFilter,
      columnPinning: this.columnPinning,
      showColumnFilters: this.showColumnFilters,
      isFullScreen: this.isFullScreen,
      sorting: this.sorting,
      includeLabels: this.includeLabels,
    }

    try {
      const formData = {
        form__url_code: this.url_code,
        state: stateSnapshot,
      }
      const response = await updateUserTableState(formData)
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de l'état de la table :",
        error
      )
    }
  }

  loadData = async (shouldSetLoading = true) => {
    if (shouldSetLoading) {
      this.loading = true
    }

    try {
      // Préparer les paramètres
      const sortId = this.sorting.length > 0 ? this.sorting[0].id : null
      const sortOrder =
        this.sorting.length > 0 && this.sorting[0].desc ? 'desc' : 'asc'
      const validFilters = {}

      this.columnFilters.forEach((filter) => {
        if (filter.value) {
          validFilters[filter.id] = filter.value
        }
      })

      // Appel API avec l'argument 'with_labels' selon includeLabels
      const response = await collectFormAnswersData(
        this.url_code,
        this.pagination.pageIndex + 1,
        this.pagination.pageSize,
        false, // Paramètre 'all_data'
        sortId,
        sortOrder,
        this.globalFilter,
        validFilters,
        this.includeLabels // Utilise l'état du store
      )

      runInAction(() => {
        this.data = response.results.rows
        this.totalRows = response.count
        this.pageCount = Math.ceil(response.count / this.pagination.pageSize)
        this.headers = this.transformHeadersToColumns(response.results.headers)
        this.headerstest = response.results.headers
        if (shouldSetLoading) {
          this.loading = false
        }
      })
    } catch (error) {
      runInAction(() => {
        if (shouldSetLoading) {
          this.loading = false
        }
      })
      console.error('Error fetching data: ', error)
    }
  }

  exportAllData = async (exportFunc) => {
    try {
      const fullData = await collectFormAnswersData(
        this.url_code, // URL code
        1, // Page index
        10, // Page size
        true, // All data
        null, // Sort ID
        'asc', // Sort Order
        '', // Global Filter
        {}, // Column Search
        this.includeLabels // Inclure ou non les labels selon l'état
      )

      runInAction(() => {
        if (fullData && fullData.rows) {
          exportFunc(fullData.rows, true)
        } else {
          console.error('Erreur lors de la récupération des données complètes.')
        }
      })
    } catch (error) {
      console.error("Erreur lors de l'exportation des données : ", error)
    }
  }

  setPageIndex(newIndex) {
    this.pagination.pageIndex = newIndex
    this.loadData()
  }

  setPageSize(newSize) {
    this.pagination.pageSize = newSize
    this.loadData()
  }

  handleTestPagination = (updater) => {
    // Vérifier si 'updater' est une fonction et essayer de l'exécuter
    console.log('Résultat de updater:', updater)
  }

  updateColumnFilters = (newFilters) => {
    const updatedFilters = []
    newFilters.forEach((newFilter) => {
      const existingFilterIndex = this.columnFilters.findIndex(
        (filter) => filter.id === newFilter.id
      )

      if (existingFilterIndex > -1) {
        // Le filtre existe déjà, le mettre à jour
        updatedFilters.push(newFilter)
      } else {
        // C'est un nouveau filtre, l'ajouter
        updatedFilters.push(newFilter)
      }
    })

    // Mettre à jour l'état du store MobX avec les filtres actuels
    this.columnFilters = updatedFilters
    this.updateTableState()
    this.pagination.pageIndex = 0
    this.loadData()
  }

  updateShowColumnFiltersChange = action((value) => {
    this.showColumnFilters = value
    this.updateTableState()
  })

  updateShowGlobalFilterChange = action((value) => {
    this.showGlobalFilter = value
    this.updateTableState()
  })

  updateColumnVisibility = (newValue) => {
    this.columnVisibility = newValue
    this.updateTableState()
  }

  updatePagination = action((pageIndex, pageSize) => {
    this.pagination.pageIndex = pageIndex
    this.pagination.pageSize = pageSize
    this.loadData()
  })

  updateColumnPinning = action((left, right) => {
    this.columnPinning.left = left
    this.columnPinning.right = right
    this.updateTableState()
  })

  updateColumnSizing = (newValue) => {
    this.columnSizing = newValue
    this.updateTableState()
  }

  updateRowSelection = (newValue) => {
    this.rowSelection = newValue
  }

  updateColumnOrder = (newValue) => {
    const updatedValue = [...this.columnOrderDefault, ...newValue]
    this.columnOrder = updatedValue
  }

  updateIsFullScreen = (newValue) => {
    this.isFullScreen = newValue
    this.updateTableState()
  }

  updateDensity = (newValue) => {
    this.density = newValue
    this.updateTableState()
  }

  updateSortingState = (newSorting) => {
    this.sorting = newSorting
    this.updateTableState()
    this.loadData()
  }

  updateGlobalFilterChange = (value) => {
    this.globalFilter = value
    this.pagination.pageIndex = 0
    this.loadData()
  }

  // Méthode pour activer/désactiver l'inclusion des labels
  toggleIncludeLabels = action(() => {
    this.includeLabels = !this.includeLabels
    this.updateTableState()
    this.loadData()
  })

  handleChangePage = action((newPage) => {
    this.pagination.pageIndex = newPage
    this.loadData()
  })

  handlePageSizeChange = action((newPageSize) => {
    this.pagination.pageSize = newPageSize
    this.loadData()
  })

  handleSearchChange = action((value) => {
    this.globalFilter = value
    this.pagination.pageIndex = 0
    this.updateTableState()
    this.loadData()
  })

  handleSortBy = action((columnId, order) => {
    this.mySortBy = { id: columnId, desc: order === 'descend' }
    this.loadData()
  })
}

const AnswersContext = React.createContext()

const AnswersProvider = observer(({ children }) => {
  const { url_code } = useParams()
  const store = useMemo(() => new AnswersStore(url_code), [url_code])
  // Ici, utilisez l'API useEffect pour exécuter des actions d'initialisation et d'autres choses qui dépendent des changements de l'état du store
  // Le store est maintenant disponible pour les enfants
  return (
    <AnswersContext.Provider value={store}>{children}</AnswersContext.Provider>
  )
})

export { AnswersContext, AnswersProvider, AnswersStore }
